/**
 * Site Configuration
 * Admin can configure the website behavior here
 */

export interface SiteConfig {
  /**
   * Site mode:
   * - 'category': Display homepage with categories (Horror Games, Action Games, etc.)
   * - 'single-game': Display a specific game as the homepage
   */
  siteMode: 'category' | 'single-game';

  /**
   * Main game slug (only used when siteMode is 'single-game')
   * Example: 'retro-bowl', 'block-blast', 'basketball-legends'
   * 
   * The system will:
   * 1. Try to load {gameSlug}.json from content/games/
   * 2. If not found, fallback to home.json (switch to category mode)
   * 3. If home.json not found, use the first available game
   */
  gameSlug?: string;

  /**
   * Site metadata
   */
  siteName: string;
  siteDescription: string;
  siteUrl: string;
}

/**
 * Game Recommendation Limits
 */
export const GAME_LIMITS = {
  SIDEBAR_GAMES: 15,           // Number of games in sidebar
  MORE_GAMES: 12,              // Number of "more games like this"
  LATEST_GAMES_POOL: 50,       // Pool size for latest games selection
  FEATURED_DISPLAY: 100,       // Max featured games to display
} as const;

/**
 * Supported Languages Configuration
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', hreflang: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', hreflang: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', hreflang: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ja', hreflang: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'pt', hreflang: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'zh', hreflang: 'zh', name: '中文', flag: '🇨🇳' },
] as const;

/**
 * Game Player Configuration
 */
export const GAME_PLAYER = {
  DEFAULT_ASPECT_RATIO: '56.25%',  // 16:9 ratio
  DEFAULT_WIDTH: '100%',
  DEFAULT_HEIGHT: 600,
  LOADING_TEXT: 'Loading game...',
  PLAY_BUTTON_TEXT: 'PLAY NOW',
} as const;

/**
 * SEO Configuration
 */
export const SEO_CONFIG = {
  DEFAULT_IMAGE: '/assets/img/default-og-image.jpg',
  TITLE_SEPARATOR: ' | ',
  TITLE_SUFFIX: 'FNAF Unblocked',

  ROBOTS_META: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  OG_LOCALE: 'en_US',
  TWITTER_CARD_TYPE: 'summary_large_image',
  TWITTER_HANDLE: '@FNAFUnblocked',

  // Specific robots meta for different page types
  ROBOTS_META_OVERRIDES: {
    'terms': 'noindex, nofollow',
    'dmca': 'noindex, nofollow',
    'privacy': 'noindex, nofollow',
    'privacy-policy': 'noindex, nofollow',
    'terms-of-service': 'noindex, nofollow',
  }
} as const;

/**
 * Helper function to get robots meta for a specific page
 * @param pageSlug - The page slug (e.g., 'terms', 'dmca', 'privacy')
 * @returns Appropriate robots meta tag content
 */
export function getRobotsMeta(pageSlug?: string): string {
  if (pageSlug && pageSlug in SEO_CONFIG.ROBOTS_META_OVERRIDES) {
    return SEO_CONFIG.ROBOTS_META_OVERRIDES[pageSlug as keyof typeof SEO_CONFIG.ROBOTS_META_OVERRIDES];
  }
  return SEO_CONFIG.ROBOTS_META;
}

/**
 * Content Loading Configuration
 */
export const CONTENT_CONFIG = {
  EXCERPT_MAX_LENGTH: 180,
  MAX_CATEGORY_TAGS: 5,
  CACHE_ENABLED: true,
} as const;

/**
 * ADMIN CONFIGURATION
 * Change these values to configure your site
 */
export const siteConfig: SiteConfig = {
  // Change to 'single-game' for single game site, or 'category' for category site
  siteMode: 'single-game',
  gameSlug: "five-nights-at-freddys",

  // Site metadata
  siteName: 'Five Nights at Freddy\'s',
  siteDescription: 'Play Five Nights at Freddy\'s(FNAF) unblocked online FREE! No download required, play instantly in browser.Survive horror nights with Freddy, Bonnie, Chica & Foxy.Best unblocked FNAF games without download - start playing now!',
  siteUrl: 'https://fivenightsatfreddysgame.io',
};

// Comments Widget Configuration
export const COMMENTS_CONFIG = {
  CSS_PATH: '/comment-sdk/comments-sdk.min.css',
  JS_PATH: '/comment-sdk/comments-sdk.min.js',
  COMMENTS_PER_PAGE: 10,
} as const;

// DOM Element IDs
export const DOM_IDS = {
  ROOT: 'root',
  COMMENTS_WIDGET: 'comments-widget',
} as const;

// Special category slugs (not regular game categories)
export const SPECIAL_CATEGORIES = ['featured', 'latest', 'trending'] as const;
export type SpecialCategory = typeof SPECIAL_CATEGORIES[number];

// Google Analytics Configuration
export const GOOGLE_ANALYTICS_ID = 'G-7F8K2DWXXG';
